import { createFileRoute, Link } from "@tanstack/react-router";
import programJourney from "@/assets/program-journey.jpg.asset.json";
import programBeforeAfter480 from "@/assets/program-before-after-480.webp.asset.json";
import programBeforeAfter832 from "@/assets/program-before-after-832.webp.asset.json";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { schemaDateFields } from "@/lib/schema-dates";

export const Route = createFileRoute("/our-program")({
  head: () => ({
    meta: [
      { title: "Comprehensive Weight Loss Program | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Comprehensive bariatric care in Northern Virginia — fellowship-trained surgeon, on-site nutritionist, certified bariatric nurses, and full support team.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Comprehensive Weight Loss Program | Virginia Bariatric" },
        { property: "og:description", content: "Comprehensive bariatric care in Northern Virginia — fellowship-trained surgeon, on-site nutritionist, certified bariatric nurses, and full support team." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/our-program" },
        { property: "og:image", content: `https://virginiabariatric.com${programJourney.url}` },
        { property: "og:image:alt", content: "Before-and-after weight-loss journey photos of Dr. Fitzer's bariatric surgery patients" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://virginiabariatric.com${programJourney.url}` },
        { name: "twitter:image:alt", content: "Before-and-after weight-loss journey photos of Dr. Fitzer's bariatric surgery patients" },
        { name: "twitter:title", content: "Comprehensive Weight Loss Program | Virginia Bariatric" },
        { name: "twitter:description", content: "Comprehensive bariatric care in Northern Virginia — fellowship-trained surgeon, on-site nutritionist, certified bariatric nurses, and full support team." },
      ] : []),
    ],
    links: [
      { rel: "canonical", href: "https://virginiabariatric.com/our-program" },
      { rel: "preload", as: "image", href: programJourney.url, fetchpriority: "high" },
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          ...schemaDateFields("/our-program"),
          url: "https://virginiabariatric.com/our-program",
          name: "Our Comprehensive Weight Loss Program",
          description:
            "Comprehensive bariatric care in Northern Virginia — fellowship-trained surgeon, on-site nutritionist, certified bariatric nurses, and full support team.",
          primaryImageOfPage: {
            "@type": "ImageObject",
            contentUrl: `https://virginiabariatric.com${programJourney.url}`,
            url: `https://virginiabariatric.com${programJourney.url}`,
            caption: "Virginia Bariatric patient shown before and after her bariatric surgery weight-loss journey",
            representativeOfPage: true,
          },
          image: [
            {
              "@type": "ImageObject",
              contentUrl: `https://virginiabariatric.com${programJourney.url}`,
              url: `https://virginiabariatric.com${programJourney.url}`,
              caption: "Virginia Bariatric patient shown before and after her bariatric surgery weight-loss journey",
            },
            {
              "@type": "ImageObject",
              contentUrl: `https://virginiabariatric.com${programBeforeAfter832.url}`,
              url: `https://virginiabariatric.com${programBeforeAfter832.url}`,
              caption: "Before-and-after photos of a Virginia Bariatric Surgery gastric sleeve patient showing dramatic weight loss",
            },
          ],
        }),
      },
    ] : [],
  }),
  component: OurProgramPage,
});

function OurProgramPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4 grid gap-3 md:gap-4 md:grid-cols-2 items-center">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div>
          <EditableText
            as="h1"
            contentKey="program.hero.title"
            defaultValue="Comprehensive Weight Loss Program"
            className="text-4xl md:text-5xl font-serif"
          />
          <div className="mt-4 md:mt-6 space-y-3 md:space-y-4 text-foreground/80 leading-[1.6] md:text-[1.125rem]">
            <EditableText
              as="p"
              contentKey="program.hero.p1"
              defaultValue="Starting your weight loss journey can be overwhelming. We want to make achieving your goal as stress-free as possible. We're on a mission to be the best weight-loss program in greater Virginia, and we will support you through every step of the process."
            />
          </div>
        </div>
        <div className="relative aspect-square">
          <div className="absolute -inset-4 rounded-2xl bg-brand-soft -z-10" />
          <img
            src={programJourney.url}
            alt="Virginia Bariatric patient shown before and after her bariatric surgery weight-loss journey"
            width={1080}
            height={1080}
            loading="eager"
            fetchPriority="high"
            className="relative rounded-xl object-cover shadow-xl w-full h-full"
          />
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="bg-brand-soft/30 py-4 md:py-5" innerClassName="space-y-3 md:space-y-4 text-foreground/80 leading-[1.6]">
          <EditableText
            as="p"
            contentKey="program.intro.p1"
            multiline
            defaultValue="Virginia Bariatric Surgery is located in Northern Virginia, just a short drive from Washington, D.C. Our patients come from across the greater metro area and beyond, including Virginia, Maryland, West Virginia, and Washington, D.C. Since opening our doors, we have cared for many thousands of patients, giving us the experience and expertise to provide exceptional bariatric surgery care from start to finish. Whether your journey involves weight-loss surgery, medication, or both, we are dedicated to providing world-class care in every interaction."
          />
          <EditableText
            as="h2"
            contentKey="program.team.title"
            defaultValue="Our team of professionals includes:"
            className="text-2xl md:text-3xl font-serif text-foreground pt-1"
          />
          <ul className="list-disc pl-5 md:pl-6 space-y-1.5 md:space-y-2 marker:text-brand">
            <li>
              A fellowship-trained ASMBS Surgeon of Excellence and MBSAQIP-Verified Bariatric Surgeon with experience in a wide range of bariatric procedures and a track record of superior outcomes
            </li>
            <li>An on-site nutritionist specialized in bariatrics to provide customized dietary guidance</li>
            <li>Experienced nursing staff, including an R.N. who is an ASMBS Certified Bariatric Nurse</li>
            <li>
              A{" "}
              <Link to="/resources/support" className="text-brand-mid underline">
                bariatric surgery support group
              </Link>
            </li>
            <li>Psychiatrists and psychologists with specialized training unique to bariatric surgery patients</li>
            <li>
              Bariatric-experienced medical specialty support in numerous medical fields, including cardiology, pulmonology, and endocrinology
            </li>
            <li>Physical therapy and fitness instruction</li>
            <li>
              Insurance-navigation staff for easy billing and understanding the{" "}
              <Link to="/self-pay-bariatric-surgery" className="text-brand-mid underline">
                self-pay surgery option
              </Link>
              .
            </li>
          </ul>
          <EditableText
            as="p"
            contentKey="program.intro.p2"
            defaultValue="Dr. Fitzer has two decades of experience in helping patients achieve their weight-loss goals. He will help you customize your weight-loss plan."
          />
          <EditableText
            as="p"
            contentKey="program.intro.p3"
            defaultValue="As your bariatric surgery team, we share a common mission to make weight loss as easy as possible for you. Our goals include:"
          />
          <ul className="list-disc pl-5 md:pl-6 space-y-1.5 md:space-y-2 marker:text-brand">
            <li>Providing unparalleled patient care in all phases of the weight loss journey, whether surgical or medical</li>
            <li>Ensuring our weight loss patients are the most satisfied and successful patients in the world</li>
          </ul>
          <EditableText
            as="p"
            contentKey="program.intro.p4"
            defaultValue="Are you ready to explore why Virginia Bariatric Surgery is the right place for your weight loss ambitions? To find out more about our bariatric weight loss program and to meet our team of bariatric surgery professionals, contact Virginia Bariatric Surgery at (703) 709-9771 or complete our contact form. We look forward to working with you!"
          />
      </PageSection>

      <PageSection className="pt-8 md:pt-10 pb-4 md:pb-5 grid gap-6 md:gap-8 md:grid-cols-2 items-center">
        <img
          src={programBeforeAfter832.url}
          srcSet={`${programBeforeAfter480.url} 480w, ${programBeforeAfter832.url} 832w`}
          sizes="(min-width: 768px) 50vw, 100vw"
          alt="Before-and-after photos of a Virginia Bariatric Surgery gastric sleeve patient showing dramatic weight loss"
          width={832}
          height={619}
          loading="lazy"
          decoding="async"
          className="rounded-xl object-cover shadow-xl w-full h-auto"
        />
        <div>
          <EditableText
            as="h2"
            contentKey="program.qualify.title"
            defaultValue="Do You Qualify for a Bariatric Procedure or Weight-Loss Medication?"
            className="text-2xl md:text-3xl font-serif"
          />
          <EditableText
            as="p"
            contentKey="program.qualify.body"
            defaultValue="Find out if surgery or medicine is right for you."
            className="mt-3 md:mt-4 text-muted-foreground md:text-[1.125rem] leading-[1.6]"
          />
          <p className="mt-3 md:mt-4 text-muted-foreground md:text-[1.125rem] leading-[1.6]">
            Review the full{" "}
            <Link to="/resources/qualifications" className="text-brand-mid underline">
              bariatric surgery requirements
            </Link>{" "}
            to see whether surgery or medication is the right path.
          </p>
          <div className="mt-4 md:mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/resources/qualifications"
              className="rounded-full bg-brand-logo px-6 md:px-7 py-2.5 md:py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
            >
              Surgery Qualifications
            </Link>
            <Link
              to="/resources/insurance-coverage"
              className="rounded-full bg-brand-logo px-6 md:px-7 py-2.5 md:py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
            >
              Verify surgery benefits
            </Link>
          </div>
        </div>
      </PageSection>

      <PageSection fullWidth className="bg-brand-soft/30 py-2.5 md:py-3" innerClassName="grid gap-3 md:gap-4 md:grid-cols-2">
          <article className="rounded-2xl bg-background p-4 md:p-5 shadow-sm">
            <EditableText
              as="h3"
              contentKey="program.seminar.title"
              defaultValue="Attend a Seminar"
              className="text-xl md:text-2xl font-serif"
            />
            <EditableText
              as="p"
              contentKey="program.seminar.body"
              defaultValue="Start your journey today with our online seminar."
              className="mt-1 md:mt-1.5 text-muted-foreground"
            />
            <div className="mt-2 md:mt-3 flex justify-center">
              <a
                href="https://vbportal.pattrax.com/events"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-primary px-6 md:px-7 py-2.5 md:py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                See Schedule
              </a>
            </div>
          </article>
          <article className="rounded-2xl bg-background p-4 md:p-5 shadow-sm">
            <EditableText
              as="h3"
              contentKey="program.support.title"
              defaultValue="Make an Office Appointment"
              className="text-xl md:text-2xl font-serif"
            />
            <EditableText
              as="p"
              contentKey="program.support.body"
              defaultValue="The greatest journeys all start with a single step."
              className="mt-1 md:mt-1.5 text-muted-foreground"
            />
            <div className="mt-2 md:mt-3 flex justify-center">
              <Link
                to="/appointments"
                className="rounded-full bg-primary px-6 md:px-7 py-2.5 md:py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Make Appointment
              </Link>
            </div>
          </article>
      </PageSection>
    </>
  );
}
